import React, { useState, useEffect } from 'react';
import { Form, Button, InputGroup, Row, Col, Modal } from 'react-bootstrap';
import SearchableCombobox from './SearchableCombobox';
import UseFieldOptions from '../hooks/UseFieldOptions';
import DateRangeWidget from './DateRangeWidget';

interface UseCasesSearchProps {
  onSearch: (query: string) => void;
  themeId?: number;
}


const GEOGRAPHY_REGIONS = [
  "Global",
  "EMEA",
  "AMER",
  "APAC"
];



type DateRangeWidgetValue = {
  preset: string;
  dateRange: { startDate: string; endDate: string };
  monthRange: { startMonth: number; startYear: number; endMonth: number; endYear: number };
  yearRange: { startYear: number; endYear: number };
};

const UseCasesSearch: React.FC<UseCasesSearchProps> = ({ onSearch, themeId }) => {
  const [searchText, setSearchText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    company: '',
    industry: '',
    technology: '',
    impact: '',
    useCaseType: '',
    performanceImprovementCategory: '',
    geography: '',
    country: '',
    tools: '',
    dateRange: { startDate: '', endDate: '' },
    datePreset: null as string | null,
    monthRange: { startMonth: 0, startYear: new Date().getFullYear(), endMonth: 0, endYear: new Date().getFullYear() },
    yearRange: { startYear: new Date().getFullYear(), endYear: new Date().getFullYear() },
    notNullField: '',
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'created_at',
    direction: 'desc'
  });
  const [hasFilters, setHasFilters] = useState(false);

  const { data: fieldOptions, isLoading: isLoadingOptions } = UseFieldOptions(themeId);


  // Define sortable fields
  const sortableFields = [
    { value: 'relevance_score', label: 'Relevance Score' },
    { value: 'credibility_score', label: 'Credibility Score' },
    { value: 'created_at', label: 'Created Date' },
    // { value: 'updated_at', label: 'Updated Date' },
    { value: 'use_case_date', label: 'Use Case Date' },
    { value: 'company', label: 'Company' },
    { value: 'industry', label: 'Industry' },
    { value: 'use_case_type', label: 'Use Case Type' },
    { value: 'performance_impact', label: 'Performance Impact' },
    { value: 'performance_improvement_category', label: 'Performance Improvement Category' },
    { value: 'geography', label: 'Geography' },
    { value: 'country', label: 'Country' },
    { value: 'tools', label: 'Tools' }
  ];

  // Check if any filters are applied
  useEffect(() => {
    const hasActiveFilters =
      filters.company !== '' ||
      filters.industry !== '' ||
      filters.technology !== '' ||
      filters.impact !== '' ||
      filters.useCaseType !== '' ||
      filters.performanceImprovementCategory !== '' ||
      filters.geography !== '' ||
      filters.country !== '' ||
      filters.tools !== '' ||
      filters.notNullField !== '' ||
      (filters.datePreset !== null && filters.dateRange.startDate !== '' && filters.dateRange.endDate !== '');
    setHasFilters(Boolean(hasActiveFilters));
  }, [filters]);

  const handleSearch = () => {
    let queryString = '';
    
    // Add basic search if exists
    if (searchText.trim() !== '') {
      queryString += `&search=${encodeURIComponent(searchText.trim())}`;
    }

    // Add theme_id if it exists
    if (themeId) {
      queryString += `&theme_id=${themeId}`;
    }

    // Add sorting parameters
    queryString += `&sort_by=${sortConfig.field}&sort_direction=${sortConfig.direction}`;
    if(sortConfig.field !== 'created_at' || sortConfig.direction !== 'desc') {
      setHasFilters(true);
    }

    // Add advanced filters if they exist
    if (filters.company) {
      queryString += `&company=${encodeURIComponent(filters.company)}`;
    }
    
    if (filters.industry) {
      queryString += `&industry=${encodeURIComponent(filters.industry)}`;
    }
    
    if (filters.technology) {
      queryString += `&technology=${encodeURIComponent(filters.technology)}`;
    }
    
    if (filters.impact) {
      queryString += `&performance_impact=${encodeURIComponent(filters.impact)}`;
    }

    if (filters.useCaseType) {
      queryString += `&use_case_type=${encodeURIComponent(filters.useCaseType)}`;
    }

    if (filters.performanceImprovementCategory) {
      queryString += `&performance_improvement_category=${encodeURIComponent(filters.performanceImprovementCategory)}`;
    }

    if (filters.geography) {
      queryString += `&geography=${encodeURIComponent(filters.geography)}`;
    }

    if (filters.country) {
      queryString += `&country=${encodeURIComponent(filters.country)}`;
    }
    
    if (filters.tools) {
      queryString += `&tools=${encodeURIComponent(filters.tools)}`;
    }

    if (filters.dateRange.startDate && filters.dateRange.endDate) {
      queryString += `&start_date=${filters.dateRange.startDate}&end_date=${filters.dateRange.endDate}`;
    }

    // Add not null field filter if set
    if (filters.notNullField) {
      queryString += `&not_null_field=${encodeURIComponent(filters.notNullField)}`;
    }

    onSearch(queryString);
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({
      company: '',
      industry: '',
      technology: '',
      impact: '',
      useCaseType: '',
      performanceImprovementCategory: '',
      geography: '',
      country: '',
      tools: '',
      dateRange: { startDate: '', endDate: '' },
      datePreset: null as string | null,
      monthRange: { startMonth: 0, startYear: new Date().getFullYear(), endMonth: 0, endYear: new Date().getFullYear() },
      yearRange: { startYear: new Date().getFullYear(), endYear: new Date().getFullYear() },
      notNullField: '',
    });
    setSortConfig({
      field: 'created_at',
      direction: 'desc'
    });
    onSearch('');
  };

  const handleApplyFilters = () => {
    handleSearch();
    setShowModal(false);
  };

  // Handle form submission to prevent page refresh
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  // Handle key press in search input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // Check if there's any search or filters applied
  var hasSearchOrFilters = searchText !== '' || hasFilters;

  return (
    <div className="">
      <Form onSubmit={handleSubmit} className='d-flex align-items-center  gap-2'>
          <InputGroup className=""> 
            <Form.Control
              placeholder="Search use cases..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleKeyPress}
              size="sm"
            />
            <Button variant="primary"  size="sm" onClick={handleSearch}>
              <i className="ri-search-2-line"></i>
            </Button>
          </InputGroup>

          <Button 
            variant={hasFilters ? "success" : "outline-secondary"}
            size="sm" 
            onClick={() => setShowModal(true)}
            title="Advanced Filters"
            style={{width: '200px'}}
          >
            <i className="ri-filter-line"></i> Advanced Filters
          </Button>
          {hasSearchOrFilters && (
            <Button 
              variant="outline-danger" 
              size="sm" 
              onClick={handleReset}
              title="Reset"
            >
              <i className="ri-close-line"></i>
            </Button>
          )}
      </Form>

      {/* Advanced Filters Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Advanced Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Sort By</Form.Label>
                <Row className="g-2">
                  <Col md={8}>
                    <Form.Select
                      value={sortConfig.field}
                      onChange={(e) => setSortConfig({ ...sortConfig, field: e.target.value })}
                    >
                      {sortableFields.map(field => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Select
                      value={sortConfig.direction}
                      onChange={(e) => setSortConfig({ ...sortConfig, direction: e.target.value })}
                    >
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </Form.Select>
                  </Col>
                </Row>
              </Form.Group>
            </Col>

            <Col md={6}>
              <SearchableCombobox
                options={fieldOptions?.useCaseTypes || []}
                value={filters.useCaseType}
                onChange={(value) => setFilters({ ...filters, useCaseType: value })}
                placeholder="Enter use case type"
                label="Use Case Type"
              />
            </Col>

            <Col md={6}>
              <SearchableCombobox
                options={fieldOptions?.companies || []}
                value={filters.company}
                onChange={(value) => setFilters({ ...filters, company: value })}
                placeholder="Enter company name"
                label="Company"
              />
            </Col>

            <Col md={6}>
              <SearchableCombobox
                options={fieldOptions?.industries || []}
                value={filters.industry}
                onChange={(value) => setFilters({ ...filters, industry: value })}
                placeholder="Enter industry"
                label="Industry"
              />
            </Col>

            <Col md={6}>
              <SearchableCombobox
                options={fieldOptions?.impacts || []}
                value={filters.impact}
                onChange={(value) => setFilters({ ...filters, impact: value })}
                placeholder="Enter impact"
                label="Performance Impact"
              />
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Region</Form.Label>
                <Form.Select
                  value={filters.geography}
                  onChange={(e) => setFilters({ ...filters, geography: e.target.value })}
                >
                  <option value="">Select a region</option>
                  {GEOGRAPHY_REGIONS.map(region => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <SearchableCombobox
                options={fieldOptions?.countries || []}
                value={filters.country}
                onChange={(value) => setFilters({ ...filters, country: value })}
                placeholder="Enter country"
                label="Country"
              />
            </Col>

            <Col md={6}>
              <SearchableCombobox
                options={fieldOptions?.tools || []}
                value={filters.tools}
                onChange={(value) => setFilters({ ...filters, tools: value })}
                placeholder="Enter tools"
                label="Tools"
              />
            </Col>

            <hr className='mt-3' />


            <Col md={12} className='mt-0'>
              <DateRangeWidget
                value={{
                  preset: filters.datePreset || '',
                  dateRange: filters.dateRange,
                  monthRange: filters.monthRange,
                  yearRange: filters.yearRange,
                }}
                onChange={(
                  { preset, dateRange, monthRange, yearRange }: DateRangeWidgetValue
                ) => {
                  setFilters({
                    ...filters,
                    datePreset: preset || null,
                    dateRange: dateRange,
                    monthRange: monthRange,
                    yearRange: yearRange,
                  });
                }}
              />
            </Col>

            <hr className='my-3' />

            <Col md={12}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="not-null-performance-impact"
                  label="Only show use cases with Performance Impact"
                  checked={filters.notNullField === 'performance_impact'}
                  onChange={(e) => setFilters({ ...filters, notNullField: e.target.checked ? 'performance_impact' : '' })}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UseCasesSearch; 